import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StationRequestsFilterState, StationRequestV2 } from '../../../types/dashboardV2';
import { FilterBar } from '../../../components/common/FilterBar/FilterBar';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface StationRequestsFilterBarProps {
  filters: StationRequestsFilterState;
  onFilterChange: (filters: Partial<StationRequestsFilterState>) => void;
  onRefresh: () => void;
  allRequests: StationRequestV2[];
}

export const StationRequestsFilterBar = memo(function StationRequestsFilterBar({
  filters,
  onFilterChange,
  onRefresh,
  allRequests,
}: StationRequestsFilterBarProps) {
  const { t } = useTranslation();

  const handleSearchChange = useCallback(
    (search: string) => onFilterChange({ search }),
    [onFilterChange],
  );

  const handleExportPDF = useCallback(() => {
    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFontSize(16);
    doc.text(t('dashboard.stationRequests.pdfTitle'), 14, 18);

    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`${t('dashboard.stationRequests.pdfGenerated')}: ${new Date().toLocaleString()}`, 14, 25);
    doc.setTextColor(0);

    const rows = allRequests.map((r) => [
      r.phone,
      r.city,
      r.comment || '—',
      r.userEmail || r.userId || '—',
      new Date(r.createdAt).toLocaleString('en-GB'),
    ]);

    autoTable(doc, {
      startY: 30,
      head: [[
        t('dashboard.stationRequests.table.phone'),
        t('dashboard.stationRequests.table.city'),
        t('dashboard.stationRequests.table.comment'),
        t('dashboard.stationRequests.table.submittedBy'),
        t('dashboard.stationRequests.table.submittedAt'),
      ]],
      body: rows,
      headStyles: {
        fillColor: [30, 30, 46],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [40, 40, 40],
      },
      alternateRowStyles: { fillColor: [245, 245, 250] },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 35 },
        2: { cellWidth: 80 },
        3: { cellWidth: 60 },
        4: { cellWidth: 45 },
      },
      margin: { top: 30, left: 14, right: 14 },
      theme: 'grid',
    });

    doc.save('station-requests.pdf');
  }, [allRequests, t]);

  return (
    <FilterBar
      search={{
        value: filters.search,
        onChange: handleSearchChange,
        placeholder: t('dashboard.stationRequests.searchPlaceholder'),
        ariaLabel: t('dashboard.stationRequests.searchPlaceholder'),
        name: 'station-requests-search',
      }}
      onRefresh={onRefresh}
      refreshAriaLabel="Refresh"
      rightAction={
        <button
          type="button"
          className="export-pdf-btn"
          onClick={handleExportPDF}
          title={t('dashboard.stationRequests.exportPDF')}
          aria-label={t('dashboard.stationRequests.exportPDF')}
          disabled={allRequests.length === 0}
        >
          <span className="material-symbols-outlined" aria-hidden="true">picture_as_pdf</span>
          {t('dashboard.stationRequests.exportPDF')}
        </button>
      }
    />
  );
});

export default StationRequestsFilterBar;
